// Implements: F-UIX-019
/**
 * The platform admin's status panel (UIX.8, OBS.12): read on opening and again when the
 * admin asks ("Actualizar"), never on a timer. While a new read is on its way the last
 * answer stays on screen, and a failed new read says the answer shown is the previous one;
 * a failed first read shows the failure with a retry.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { Card } from "../../../shared/ui/atoms/Card";
import { Skeleton } from "../../../shared/ui/atoms/Skeleton";
import styles from "../components/StatusPanel.module.css";
import { StatusPanel } from "../components/StatusPanel";
import { useSystemStatus } from "../hooks/useSystemStatus";

export function SystemStatusContainer(): ReactElement {
  const { t } = useTranslation();
  const status = useSystemStatus();
  const refetch = (): void => {
    void status.refetch();
  };

  if (status.data !== undefined) {
    return (
      <StatusPanel
        view={status.data}
        refreshing={status.isFetching}
        refreshFailed={status.isError}
        onRefresh={refetch}
      />
    );
  }
  if (status.isError) return <ErrorState error={status.error} onRetry={refetch} />;
  // The status list's shape, announced once (UX-11).
  return (
    <div className={styles.skeleton} role="status" aria-busy="true" aria-label={t("admin.status.loading")}>
      <Card padded>
        <div className={styles.skeletonRows}>
          {[0, 1, 2, 3].map((row) => (
            <Skeleton key={row} />
          ))}
        </div>
      </Card>
      <Skeleton shape="block" />
    </div>
  );
}
